import { useState, useEffect, useMemo } from 'react';
import { getLeadExecutions } from '../services/leadExecutionService';
import toast from 'react-hot-toast';

export const useLeadExecutions = () => {
  const [allExecutions, setAllExecutions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // Pagination & Filtering state
  const [page, setPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('All statuses');
  const itemsPerPage = 10;

  const fetchExecutions = async () => {
    try {
      setIsLoading(true);
      const data = await getLeadExecutions();
      setAllExecutions(data.data || []);
    } catch (error) {
      console.error('Error fetching lead executions:', error);
      toast.error('Failed to load lead executions');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchExecutions();
  }, []);

  // Filter logic
  const filteredExecutions = useMemo(() => {
    return allExecutions.filter(ex => {
      let matches = true;
      if (statusFilter !== 'All statuses' && statusFilter !== 'all') {
        matches = matches && (ex.status === statusFilter || ex.leadId?.status === statusFilter);
      }
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const verveId = (ex.leadId?.verveId || '').toLowerCase();
        const name = `${ex.leadId?.firstName || ''} ${ex.leadId?.lastName || ''}`.toLowerCase();
        matches = matches && (verveId.includes(query) || name.includes(query));
      }
      return matches;
    });
  }, [allExecutions, statusFilter, searchQuery]);

  // Pagination logic
  const totalItems = filteredExecutions.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage) || 1;
  const paginatedExecutions = filteredExecutions.slice((page - 1) * itemsPerPage, page * itemsPerPage);

  // Stats calculation
  const stats = useMemo(() => {
    const totalExecutions = allExecutions.length;
    const completedExecutions = allExecutions.filter(e => e.status === 'Completed').length;
    const totalRevenue = allExecutions.reduce((acc, curr) => acc + (curr.projectValue || curr.leadId?.quotationAmount || curr.leadId?.budget || 0), 0);
    const totalBalance = allExecutions.reduce((acc, curr) => acc + ((curr.projectValue || curr.leadId?.quotationAmount || curr.leadId?.budget || 0) - (curr.amountReceived || 0)), 0);

    return { totalExecutions, completedExecutions, totalRevenue, totalBalance };
  }, [allExecutions]);

  return {
    executions: paginatedExecutions, // Return paginated items directly to the table
    allExecutions,
    stats,
    isLoading,
    refetch: fetchExecutions,
    
    // UI states
    page,
    setPage,
    totalPages,
    totalItems,
    searchQuery,
    setSearchQuery: (query) => { setSearchQuery(query); setPage(1); },
    statusFilter,
    setStatusFilter: (status) => { setStatusFilter(status); setPage(1); },
    
    // Add additional mock functions to prevent errors when plugging in FilterSection
    customerName: '',
    setCustomerName: () => {},
    dateRange: { from: '', to: '' },
    datePreset: 'all',
    handleStartDateChange: () => {},
    handleEndDateChange: () => {},
    handleDatePreset: () => {},
    customerList: [],
    statusList: [...new Set(allExecutions.map(e => e.status).filter(Boolean))],
    handleExportProjects: () => toast.success('Export triggered')
  };
};
